import { el, btn, backButton, bigCupSvg } from '@/ui/dom';
import { LEADERBOARD_ENABLED } from '@/config/features';
import type { SaveData } from '@/core/SaveData';
import type { AccountState, TrophyCounts } from '@/account/AccountService';
import { nextSkillTier, skillTier, spBalance } from '@/core/skillPoints';
import { nativeShims } from '@/native/shims';
import { isValidUsername, USERNAME_MAX, USERNAME_MIN } from '@/account/racerNames';

/**
 * AccountScreen.ts — RACER ID (M100). One screen, four faces:
 *   home     — signed out: the pitch (bank SP · world rankings · cross-device)
 *              + CONTINUE WITH GOOGLE / APPLE / EMAIL. Shows the guest pot
 *              ("N SP WAITING") when there is one.
 *   email    — enter the address, send the magic link.
 *   sent     — check your inbox: tap the link on THIS device, or type the
 *              6-digit code (the code works from anywhere — the PKCE-proof
 *              path, and the future Capacitor path).
 *   signedIn — identity + wallet + sync state + SIGN OUT.
 *
 * M65 editorial language, M92 colour rule (gold = affirmative; red only on
 * the destructive SIGN OUT). Every dynamic string renders via textContent.
 */

export interface AccountCallbacks {
  onGoogle: () => Promise<string | null>;
  onApple: () => Promise<string | null>;
  onEmail: (email: string) => Promise<string | null>;
  onCode: (email: string, code: string) => Promise<string | null>;
  onBack: () => void;
  /** M101 — open the share sheet with MY referral link (sharer-only rewards:
   *  20 SP per friend who creates a Racer ID via it, capped at 5). */
  onShare: () => Promise<'shared' | 'copied' | 'dismissed'>;
  /** Racer ID dashboard rename — live availability while typing (true free /
   *  false taken / null unknown) and the unique-username claim itself. The
   *  home-screen module no longer edits the name; this is its one home. */
  onCheckName?: (name: string) => Promise<boolean | null>;
  onClaimName?: (name: string) => Promise<{ ok: boolean; reason: string }>;
  /** The trophy cabinet's counts (gold/silver/bronze for races and tours),
   *  read from the account. null = couldn't ask. */
  loadTrophies?: () => Promise<TrophyCounts | null>;
  /** M140: RACER CLUB (RACE CREW until M141) — the dashboard's second way in
   *  (owner rule: reached from PLAY ONLINE and from here). */
  onRaceCrew?: () => void;
  /** M156: the Community Rules + privacy policy every new Racer ID agrees to
   *  (App Store 1.2 — the terms must say offensive names and abuse are not
   *  tolerated, and players must agree to them before they post a name). */
  onRules?: () => void;
  onPrivacy?: () => void;
}

/** M156: an inline text link inside the legal line (a real button, so it is
 *  reachable by keyboard and VoiceOver). */
function legalLink(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'acct-legal-link';
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

/** M140: the RACER CLUB button's shield glyph (matches the hub card). */
const CREW_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path d="M12 1.8 21 4.6v6.1c0 5.5-3.8 9.9-9 11.5-5.2-1.6-9-6-9-11.5V4.6z" opacity=".35"/>' +
  '<path d="M12 4.2c-3.2 0-5.4 2.3-5.4 5.3 0 1 .3 1.9.6 2.6l-.4 1.9 2-.4c.4.2.9.3 1.4.3h5.5c1 0 1.7-.8 1.7-1.7V9.5c0-3-2.2-5.3-5.4-5.3Zm4 5.2h-3.6c-.6 0-1.1.5-1.1 1.1 0 .6.5 1.1 1.1 1.1H16v.6c0 .3-.2.5-.5.5h-3.2c-1.3 0-2.3-1-2.3-2.2s1-2.2 2.3-2.2h3.5c.1.4.2.7.2 1.1Z"/></svg>';

const GOOGLE_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.6.75 3.8 3.27 1.96 6.96l3.66 2.84C6.5 7.13 9.02 5.04 12 5.04Z"/><path fill="#4285F4" d="M23.25 12.26c0-.92-.08-1.6-.26-2.31H12v4.36h6.44c-.13 1.08-.83 2.7-2.39 3.79l3.57 2.77c2.14-1.97 3.63-4.88 3.63-8.61Z"/><path fill="#FBBC05" d="M5.63 14.2a6.9 6.9 0 0 1-.38-2.2c0-.77.14-1.51.36-2.2L1.96 6.96A11.24 11.24 0 0 0 .75 12c0 1.81.43 3.53 1.21 5.04l3.67-2.84Z"/><path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.73l-3.57-2.77c-.96.67-2.24 1.13-3.89 1.13-2.98 0-5.5-2.01-6.38-4.68l-3.66 2.84C3.8 20.73 7.6 23.25 12 23.25Z"/></svg>';
const APPLE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M16.94 12.7c-.03-2.55 2.08-3.77 2.17-3.83-1.18-1.73-3.02-1.97-3.68-2-1.56-.16-3.05.92-3.84.92-.8 0-2.02-.9-3.32-.87-1.7.03-3.28 1-4.16 2.53-1.78 3.08-.45 7.64 1.28 10.14.84 1.22 1.85 2.6 3.17 2.55 1.27-.05 1.75-.82 3.29-.82 1.53 0 1.97.82 3.31.8 1.37-.03 2.24-1.25 3.07-2.48.97-1.42 1.37-2.79 1.39-2.86-.03-.02-2.66-1.02-2.68-4.08ZM14.4 4.94c.7-.85 1.17-2.03 1.04-3.19-1 .04-2.24.67-2.96 1.52-.65.75-1.22 1.95-1.07 3.1 1.13.09 2.28-.57 3-1.43Z"/></svg>';
const MAIL_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 5.5h18a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1Zm1.4 2.06v9.38h15.2V7.56L12 13.34 4.4 7.56ZM5.9 7l6.1 4.63L18.1 7H5.9Z"/></svg>';
const SHARE_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<circle cx="6" cy="12" r="2.6"/><circle cx="17.5" cy="5.6" r="2.6"/><circle cx="17.5" cy="18.4" r="2.6"/>' +
  '<path d="m8.3 10.9 6.9-3.9 1 1.7-6.9 3.9zM8.3 13.1l6.9 3.9 1-1.7-6.9-3.9z"/></svg>';

/** The RANK emblem — winged shield under a crown, tinted by the skill tier
 *  (currentColor) with gold trim. */
const RANK_SVG =
  '<svg viewBox="0 0 120 80" aria-hidden="true">' +
  '<defs><linearGradient id="ridGold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#fff1b8"/><stop offset=".55" stop-color="#f3bd3a"/><stop offset="1" stop-color="#a86f12"/></linearGradient></defs>' +
  '<path fill="url(#ridGold)" d="M44 34 6 26l10 7-8 1 13 6-8 2 15 5-6 2 22 4zM76 34l38-8-10 7 8 1-13 6 8 2-15 5 6 2-22 4z"/>' +
  '<path fill="url(#ridGold)" d="M60 18l20 7v17c0 13-9 23-20 28-11-5-20-15-20-28V25z"/>' +
  '<path fill="currentColor" d="M60 23l15 5v14c0 10-7 18-15 22-8-4-15-12-15-22V28z"/>' +
  '<path fill="#fff" fill-opacity=".9" d="M60 31l2.6 5.4 5.9.8-4.3 4.1 1 5.9L60 44.4l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8z"/>' +
  '<path fill="url(#ridGold)" d="M48 15l5 4 7-9 7 9 5-4-2 9H50z"/>' +
  '</svg>';
const PENCIL_SVG =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M15.6 4.2 19.8 8.4 9 19.2 4 20l.8-5zM17 2.8l1.4-1.4a1.4 1.4 0 0 1 2 0l2.2 2.2a1.4 1.4 0 0 1 0 2L21.2 7z"/></svg>';
const CHEVRON_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" d="m9 5 7 7-7 7"/></svg>';

const RESEND_COOLDOWN_S = 30;

export class AccountScreen {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private face: 'home' | 'email' | 'sent' = 'home';
  private emailValue = '';
  private busy = false;
  private resendAt = 0;
  private save: SaveData | null = null;
  private state: AccountState | null = null;
  /** M111: the app-shell boot gate — signed-out faces lose their way out
   *  (apps REQUIRE a Racer ID; the signed-in face keeps BACK as normal). */
  private required = false;
  /** Home-modernize round: opened from the title's LOG IN button — the
   *  signed-out face speaks to a returning racer (same sign-in buttons). */
  private loginVoice = false;
  /** M114b — the shared corner chevron + whatever "back" means on this face. */
  private readonly backBtn: HTMLButtonElement;
  private backAction: () => void = () => this.cb.onBack();
  /** The dashboard's name editor is open — a background sync repaint must
   *  not wipe what the player is typing (it is applied when they finish). */
  private editingName = false;
  /** The trophy cabinet: last counts read from the account (null = not yet /
   *  couldn't ask), and a token so a stale answer never paints over a newer. */
  private trophies: TrophyCounts | null = null;
  private trophySeq = 0;
  /** M140: pending club/friend activity, shown on the RACER CLUB button. */
  private crewBadgeCount = 0;

  constructor(
    parent: HTMLElement,
    private cb: AccountCallbacks,
  ) {
    this.root = el('div', 'screen screen-account screen-cine', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'acct-panel', this.root);
    // M114b — ONE persistent corner chevron. It lives on the ROOT (render()
    // replaces the panel's children per face) and its action follows the face
    // via `backAction`. CRITICAL: under the M111 boot gate the signed-out home
    // face HIDES it, so `required` mode still has no way out — no button and,
    // because backNav resolves by visibility, no swipe either.
    this.backBtn = backButton(this.root, () => {
      if (this.busy) return; // a provider call is in flight
      this.backAction();
    });
  }

  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  show(save: SaveData, state: AccountState, opts?: { required?: boolean; login?: boolean }): void {
    this.save = save;
    this.state = state;
    this.face = 'home';
    this.busy = false;
    this.editingName = false;
    // A different account may have signed in since — never show its counts.
    this.trophies = null;
    this.required = opts?.required === true;
    this.loginVoice = opts?.login === true;
    this.render();
    this.loadTrophies();
    this.root.style.display = '';
  }

  /** Live account-state changes repaint the open screen in place (e.g. the
   *  magic link completing in this tab flips "sent" → the signed-in face). */
  refresh(save: SaveData, state: AccountState): void {
    if (!this.isVisible) return;
    const syncFinished = this.state?.syncing === true && !state.syncing;
    this.save = save;
    this.state = state;
    // A finished sync may have posted new podiums — re-count them.
    if (syncFinished || (state.status === 'signedIn' && this.trophies === null)) {
      this.loadTrophies();
    }
    if (this.editingName) return;
    this.render();
  }

  /** Ask the account for the trophy counts; repaint when they land. */
  private loadTrophies(): void {
    const load = this.cb.loadTrophies;
    if (!load || this.state?.status !== 'signedIn') return;
    const seq = ++this.trophySeq;
    void load().then((t) => {
      if (seq !== this.trophySeq || !t) return;
      this.trophies = t;
      if (this.isVisible && !this.editingName) this.render();
    });
  }

  private render(): void {
    const save = this.save;
    const state = this.state;
    if (!save || !state) return;
    this.panel.replaceChildren();

    const signedInFace = state.status === 'signedIn';
    // The signed-in face is the RACER ID dashboard: it sits on the Dribble
    // Racer HQ photo and brings its own header (the racer hero).
    this.root.classList.toggle('acct-dash-mode', signedInFace);
    this.root.classList.toggle('reduced', save.settings.reducedMotion);
    if (signedInFace) {
      this.backAction = () => this.cb.onBack();
      this.backBtn.style.display = '';
      this.renderSignedIn(save, state);
      return;
    }

    const head = el('div', 'acct-head', this.panel);
    const kicker = el('div', 'lb-kicker', head);
    el('span', 'mhead-rule', kicker);
    el('span', 'lb-kicker-text', kicker).textContent = 'RACER ID';
    el('span', 'mhead-rule', kicker);

    // M114b — point the ONE corner chevron at this face's "back". The email /
    // sent faces step BACK INSIDE the flow (they stay available under the
    // boot gate); only the signed-out home face is gated.
    const signedIn = state.status === 'signedIn';
    if (signedIn || this.face === 'home') this.backAction = () => this.cb.onBack();
    else if (this.face === 'email')
      this.backAction = () => {
        this.face = 'home';
        this.render();
      };
    else
      this.backAction = () => {
        this.face = 'email';
        this.render();
      };
    this.backBtn.style.display = !signedIn && this.required && this.face === 'home' ? 'none' : '';

    if (this.face === 'email') this.renderEmail(head);
    else if (this.face === 'sent') this.renderSent(head);
    else this.renderHome(save, head);
  }

  // ---- faces ---------------------------------------------------------------

  private renderHome(save: SaveData, head: HTMLElement): void {
    const title = el('div', 'lb-title', head);
    // NBSP keeps "RACER ID" together — a lone wrapped "ID" reads broken.
    const heading = this.loginVoice ? 'LOG IN' : 'CREATE YOUR RACER\u00a0ID';
    title.textContent = heading;
    title.dataset.text = heading;
    // M112 (owner): the "ONE ID · EVERY DEVICE" line is gone — the app-gate
    // note survives on native, where the sub explains WHY the screen fronted.
    if (this.loginVoice && !this.required) {
      // Google / Apple / email serve sign-up AND sign-in, so a newcomer who
      // tapped LOG IN is never stuck: say so in one line.
      el('div', 'lb-sub', head).textContent =
        'WELCOME BACK, RACER. NEW HERE? THESE MAKE YOUR RACER ID TOO';
    } else if (this.required) {
      el('div', 'lb-sub', head).textContent = 'THE APP RUNS ON YOUR RACER ID — SIGN IN TO RACE';
    }

    // M112 (owner): the three perk modules are replaced by ONE emotional
    // hero — the World Tour Cup itself, in the chrome/gold logotype voice.
    const hero = el('div', 'acct-hero', this.panel);
    const img = document.createElement('img');
    img.className = 'acct-hero-cup';
    img.alt = '';
    img.src = `${import.meta.env.BASE_URL}lobby/trophy.webp`;
    img.addEventListener('error', () => img.remove());
    hero.appendChild(img);
    const lines = el('div', 'acct-hero-lines', hero);
    const l1 = el('div', 'acct-hero-chrome', lines);
    l1.textContent = 'TOUR THE WORLD.';
    l1.dataset.text = 'TOUR THE WORLD.';
    const l2 = el('div', 'acct-hero-gold', lines);
    l2.textContent = 'BRING HOME THE CUP!';
    l2.dataset.text = 'BRING HOME THE CUP!';

    if (save.guestPoints > 0) {
      const pot = el('div', 'acct-pot', this.panel);
      el('span', 'acct-pot-num', pot).textContent = `${save.guestPoints} XP & SP`;
      el('span', 'acct-pot-label', pot).textContent =
        'WAITING IN YOUR GUEST POT — SIGN IN TO CLAIM';
    }

    const buttons = el('div', 'acct-providers', this.panel);
    const err = el('div', 'acct-error', this.panel);
    const provider = (
      html: string,
      label: string,
      cls: string,
      fn: () => Promise<string | null>,
    ): void => {
      const b = btn('', `btn acct-provider ${cls}`, buttons, () => {
        if (this.busy) return;
        this.setBusy(buttons, true);
        err.textContent = '';
        void fn().then((e) => {
          this.setBusy(buttons, false);
          if (e) err.textContent = e;
        });
      });
      const ic = el('span', 'acct-provider-ic', b);
      ic.innerHTML = html;
      el('span', 'acct-provider-label', b).textContent = label;
    };
    provider(GOOGLE_SVG, 'CONTINUE WITH GOOGLE', 'acct-google', () => this.cb.onGoogle());
    provider(APPLE_SVG, 'CONTINUE WITH APPLE', 'acct-apple', () => this.cb.onApple());
    provider(MAIL_SVG, 'CONTINUE WITH EMAIL', 'acct-email', () => {
      this.face = 'email';
      this.render();
      return Promise.resolve(null);
    });

    // M134: "nothing else" stopped being true the day Shop Point packs went
    // on sale (a purchase record is kept for buyers), and this line has to
    // agree with the privacy policy and the store's privacy labels.
    const legal = el('div', 'acct-legal', this.panel);
    legal.append(
      'One tap creates your Racer ID. We store your email, your game progress and any purchases you make. No passwords, ever. By continuing you agree to our ',
    );
    legal.appendChild(legalLink('Community Rules', () => this.cb.onRules?.()));
    legal.append(' (no offensive names or abuse) and ');
    legal.appendChild(legalLink('Privacy Policy', () => this.cb.onPrivacy?.()));
    legal.append('.');

    // M111/M114b: under the boot gate there is no way around the sign-in
    // (the app requires a Racer ID) — render() hides the corner chevron on
    // this face, so there is no button AND no swipe out.
  }

  private renderEmail(head: HTMLElement): void {
    const title = el('div', 'lb-title', head);
    title.textContent = 'SIGN IN WITH EMAIL';
    title.dataset.text = 'SIGN IN WITH EMAIL';
    el('div', 'lb-sub', head).textContent = 'WE SEND A MAGIC LINK + A 6-DIGIT CODE';

    const form = el('div', 'acct-form', this.panel);
    const input = document.createElement('input');
    input.type = 'email';
    input.className = 'acct-input';
    input.placeholder = 'you@example.com';
    input.autocomplete = 'email';
    input.spellcheck = false;
    input.value = this.emailValue;
    input.addEventListener('keydown', (e) => {
      e.stopPropagation(); // menu keys must never steer the attract cam
      if (e.key === 'Enter') send();
    });
    form.appendChild(input);
    const err = el('div', 'acct-error', form);
    const sendBtn = btn('SEND MAGIC LINK', 'btn btn-primary acct-send', form, () => send());
    const send = (): void => {
      if (this.busy) return;
      const email = input.value.trim();
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        err.textContent = 'That email doesn’t look right.';
        return;
      }
      this.emailValue = email;
      this.busy = true;
      sendBtn.disabled = true;
      err.textContent = '';
      void this.cb.onEmail(email).then((e) => {
        this.busy = false;
        sendBtn.disabled = false;
        if (e) {
          err.textContent = e;
          return;
        }
        this.resendAt = Date.now() + RESEND_COOLDOWN_S * 1000;
        this.face = 'sent';
        this.render();
      });
    };

    // M114b: BACK is the corner chevron (render() points it at the home face).
    input.focus();
  }

  private renderSent(head: HTMLElement): void {
    const title = el('div', 'lb-title', head);
    title.textContent = 'CHECK YOUR INBOX';
    title.dataset.text = 'CHECK YOUR INBOX';
    const sub = el('div', 'lb-sub', head);
    sub.textContent = `SENT TO ${this.emailValue.toUpperCase()}`;

    const note = el('div', 'acct-note', this.panel);
    // M111: in the app the emailed LINK opens the app via its deep link on
    // this phone — but the 6-digit code is the sure thing, so it leads.
    note.textContent = nativeShims.platform
      ? 'Type the 6-digit code from the email below — or tap the email’s link on this phone to jump straight back into the app.'
      : 'Tap the link on THIS device to sign straight in — or type the 6-digit code from the email below (works from any device).';

    const form = el('div', 'acct-form', this.panel);
    const input = document.createElement('input');
    input.className = 'acct-input acct-code';
    input.inputMode = 'numeric';
    input.autocomplete = 'one-time-code';
    input.maxLength = 6;
    input.placeholder = '••••••';
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') verify();
    });
    form.appendChild(input);
    const err = el('div', 'acct-error', form);
    const verifyBtn = btn('VERIFY CODE', 'btn btn-primary acct-send', form, () => verify());
    const verify = (): void => {
      if (this.busy) return;
      const code = input.value.trim();
      if (!/^\d{6}$/.test(code)) {
        err.textContent = 'The code is the 6 digits from the email.';
        return;
      }
      this.busy = true;
      verifyBtn.disabled = true;
      err.textContent = '';
      void this.cb.onCode(this.emailValue, code).then((e) => {
        this.busy = false;
        verifyBtn.disabled = false;
        if (e) err.textContent = e; // success: the signed-in refresh repaints
      });
    };

    const foot = el('div', 'acct-foot', this.panel);
    const resend = btn('RESEND', 'btn btn-ghost', foot, () => {
      if (Date.now() < this.resendAt || this.busy) return;
      this.busy = true;
      void this.cb.onEmail(this.emailValue).then((e) => {
        this.busy = false;
        this.resendAt = Date.now() + RESEND_COOLDOWN_S * 1000;
        if (e) err.textContent = e;
        tickResend();
      });
    });
    const tickResend = (): void => {
      const left = Math.ceil((this.resendAt - Date.now()) / 1000);
      if (left > 0 && this.isVisible && this.face === 'sent') {
        resend.textContent = `RESEND (${left})`;
        resend.disabled = true;
        window.setTimeout(tickResend, 500);
      } else {
        resend.textContent = 'RESEND';
        resend.disabled = false;
      }
    };
    tickResend();
    // M114b: BACK is the corner chevron (render() points it at the email face).
    input.focus();
  }

  /**
   * The RACER ID dashboard (owner design rounds, after the inspiration board):
   * a gold-framed card on the HQ photo. The hero is the racer in the HQ
   * (public/account/racer.webp, a swappable drop-in) carrying WELCOME BACK,
   * the username (renamed here — the pencil) and the RANK emblem beside it;
   * under it compact XP · SP, the TROPHY CABINET (gold / silver / bronze
   * finishes for races and for tours) and INVITE FRIENDS (electric blue, the
   * M101 referral loop). Owner round 2 removed SYNC and GET RACING!.
   */
  private renderSignedIn(save: SaveData, state: AccountState): void {
    // M103: signed in, the server username IS the identity — it leads.
    const name = (state.nickname || save.nickname || 'RACER').toUpperCase();
    const card = el('div', 'rid-card', this.panel);

    // ---- hero ----
    const hero = el('div', 'rid-hero', card);
    const img = el('img', 'rid-hero-img', hero);
    img.alt = '';
    img.decoding = 'async';
    img.src = `${import.meta.env.BASE_URL}account/racer.webp`;
    img.onerror = () => img.remove(); // the card's own dark base stands in
    el('div', 'rid-hero-scrim', hero);
    const copy = el('div', 'rid-hero-copy', hero);
    el('div', 'rid-kicker', copy).textContent = 'RACER ID';
    el('div', 'rid-welcome', copy).textContent = 'WELCOME BACK,';
    const nameRow = el('div', 'rid-name-row', copy);
    const nameEl = el('div', 'rid-name', nameRow);
    nameEl.textContent = name;
    // Long usernames step the display size down so they never run into the
    // racer (usernames are 3-12 characters).
    if (name.length > 9) nameEl.classList.add('rid-name-long');
    else if (name.length > 6) nameEl.classList.add('rid-name-mid');
    // The rename lives HERE (owner: not on the home-screen module).
    const claim = this.cb.onClaimName;
    if (claim) {
      const edit = el('button', 'rid-edit', nameRow);
      edit.type = 'button';
      edit.setAttribute('aria-label', 'Change your Racer ID name');
      edit.innerHTML = PENCIL_SVG;
      edit.addEventListener('click', () =>
        this.openNameEditor(copy, nameRow, state.nickname || save.nickname || '', claim),
      );
    }

    // RANK — the skill tier's emblem, right under the name (owner round 2).
    const xp = save.lifetimePoints;
    const tier = skillTier(xp);
    const next = nextSkillTier(xp);
    const rank = el('div', 'rid-rank', copy);
    const emblem = el('span', 'rid-emblem', rank);
    emblem.innerHTML = RANK_SVG;
    emblem.style.color = tier.color;
    const rankText = el('span', 'rid-rank-text', rank);
    el('span', 'rid-rank-label', rankText).textContent = 'RANK';
    const tierName = el('span', 'rid-tier', rankText);
    tierName.textContent = tier.name;
    tierName.style.color = tier.color;
    if (LEADERBOARD_ENABLED && state.rank) {
      el('span', 'rid-rank-world', rankText).textContent = `#${state.rank} WORLDWIDE`;
    }

    const providerName =
      state.provider === 'google' ? 'GOOGLE' : state.provider === 'apple' ? 'APPLE' : 'EMAIL';
    const via = el('div', 'rid-via', copy);
    via.textContent = `SIGNED IN WITH ${providerName}`;
    if (state.email) {
      el('span', 'rid-via-email', via).textContent = state.email;
    }

    // ---- body: XP · SP, then the trophy cabinet ----
    const body = el('div', 'rid-body', card);
    const stats = el('div', 'rid-stats', body);
    const stat = (label: string, cls: string): HTMLDivElement => {
      const c = el('div', `rid-stat ${cls}`, stats);
      el('div', 'rid-stat-label', c).textContent = label;
      return c;
    };
    // XP — progress: rank + garage unlocks. The bar runs to the next tier.
    const xpCell = stat('XP', 'rid-xp');
    el('div', 'rid-stat-value', xpCell).textContent = xp.toLocaleString('en-US');
    const bar = el('div', 'rid-bar', xpCell);
    const fill = el('span', 'rid-bar-fill', bar);
    const frac = next ? (xp - tier.min) / Math.max(1, next.min - tier.min) : 1;
    fill.style.width = `${Math.round(Math.min(1, Math.max(0, frac)) * 100)}%`;
    el('div', 'rid-stat-unit', xpCell).textContent = next
      ? `${(next.min - xp).toLocaleString('en-US')} XP TO ${next.name}`
      : 'TOP TIER';
    // SP — the shop's balance (always green + its own name, M131).
    const spCell = stat('SP', 'rid-sp');
    el('div', 'rid-stat-value', spCell).textContent = spBalance(save).toLocaleString('en-US');
    el('div', 'rid-stat-unit', spCell).textContent = 'SHOP POINTS';

    this.renderTrophies(body);

    // ---- invite + foot ----
    // M101: the referral loop's permanent home — hidden in Big Kid (accounts
    // are never advertised there) and until the wallet delivers the code.
    const foot = el('div', 'rid-foot', card);
    const bigkid = save.settings.difficulty === 'bigkid';
    // M140: RACER CLUB sits beside INVITE FRIENDS (two side by side; stacked
    // on narrow phones). Never in Big Kid (no accounts / online there).
    const crewBtnShown = !bigkid && !!this.cb.onRaceCrew;
    if (crewBtnShown || (!bigkid && state.referralCode)) {
      const actions = el('div', 'rid-actions', card);
      card.insertBefore(actions, foot);
      if (crewBtnShown) {
        const crew = btn('', 'btn rid-btn rid-crew', actions, () => this.cb.onRaceCrew?.());
        el('span', 'rid-crew-ic', crew).innerHTML = CREW_SVG;
        el('span', 'rid-btn-label', crew).textContent = 'RACER CLUB';
        const badge = el('span', 'rid-crew-badge', crew);
        badge.textContent = this.crewBadgeCount > 9 ? '9+' : String(this.crewBadgeCount);
        badge.style.display = this.crewBadgeCount > 0 ? '' : 'none';
        el('span', 'rid-btn-chev', crew).innerHTML = CHEVRON_SVG;
      }
    }
    if (!bigkid && state.referralCode) {
      const actions = card.querySelector<HTMLDivElement>('.rid-actions') as HTMLDivElement;
      actions.classList.toggle('rid-actions-2', crewBtnShown);
      const invite = btn('', 'btn btn-share rid-btn rid-share', actions, () => {
        void this.cb.onShare().then((how) => {
          if (how === 'copied') feedback.textContent = 'LINK COPIED — SEND IT ANYWHERE';
          else if (how === 'shared') feedback.textContent = 'SENT!';
        });
      });
      el('span', 'ob-share-ic', invite).innerHTML = SHARE_SVG;
      el('span', 'rid-btn-label', invite).textContent = 'INVITE FRIENDS';
      el('span', 'rid-btn-chev', invite).innerHTML = CHEVRON_SVG;
      const sub = el('div', 'rid-foot-line', foot);
      el('span', 'rid-foot-rule', sub);
      el('span', 'rid-foot-text', sub).textContent =
        state.refClaimed > 0
          ? `${state.refClaimed}/5 FRIENDS JOINED · +${state.refPoints} XP & SP EARNED`
          : 'EARN 20 XP & SP PER FRIEND WHO JOINS · UP TO 5 FRIENDS';
      el('span', 'rid-foot-rule', sub);
      const feedback = el('div', 'ob-share-feedback', foot);
      feedback.setAttribute('aria-live', 'polite');
    }

    // M102b: SIGN OUT lives in SETTINGS (the one home — owner rule).
    el('div', 'rid-legal', foot).textContent =
      'Sign out or delete your Racer ID in SETTINGS → RACER ID. Your career stays safe on your account.';
  }

  /** The TROPHY CABINET: gold / silver / bronze cups with the number of
   *  podium finishes, one row for single races and one for whole tours.
   *  Counts read "–" until the account answers. */
  private renderTrophies(parent: HTMLElement): void {
    const box = el('div', 'rid-trophies', parent);
    const head = el('div', 'rid-trophies-head', box);
    el('span', 'rid-foot-rule', head);
    el('span', 'rid-trophies-title', head).textContent = 'TROPHY CABINET';
    el('span', 'rid-foot-rule', head);
    const kinds = ['gold', 'silver', 'bronze'] as const;
    const places = ['1ST', '2ND', '3RD'];
    const row = (label: string, counts: readonly number[] | null): void => {
      const r = el('div', 'rid-trophy-row', box);
      el('div', 'rid-trophy-label', r).textContent = label;
      const cups = el('div', 'rid-trophy-cups', r);
      kinds.forEach((kind, i) => {
        const n = counts ? counts[i] : null;
        const cell = el('div', `rid-trophy rid-trophy-${kind}`, cups);
        if (n === 0) cell.classList.add('rid-trophy-none');
        cell.setAttribute('aria-label', `${places[i]} place: ${n ?? 'loading'}`);
        el('span', 'rid-trophy-cup', cell).innerHTML = bigCupSvg(kind);
        const txt = el('span', 'rid-trophy-txt', cell);
        el('span', 'rid-trophy-n', txt).textContent = n === null ? '–' : n.toLocaleString('en-US');
        el('span', 'rid-trophy-place', txt).textContent = places[i];
      });
    };
    row('RACE VICTORIES', this.trophies?.race ?? null);
    row('TOUR VICTORIES', this.trophies?.tour ?? null);
  }

  /**
   * The dashboard name editor: the big name becomes an input with a live
   * AVAILABLE / TAKEN line under it and SAVE (gold) · CANCEL. Saving claims
   * the globally-unique username (M101); a refusal says why and keeps the
   * editor open so the player can try another.
   */
  private openNameEditor(
    copy: HTMLElement,
    nameRow: HTMLElement,
    current: string,
    claim: (name: string) => Promise<{ ok: boolean; reason: string }>,
  ): void {
    if (this.editingName) return;
    this.editingName = true;
    nameRow.style.display = 'none';
    const via = copy.querySelector<HTMLElement>('.rid-via');
    if (via) via.style.display = 'none';

    const form = document.createElement('form');
    form.className = 'rid-rename';
    nameRow.after(form);
    const input = el('input', 'rid-rename-input', form);
    input.value = current;
    input.maxLength = USERNAME_MAX;
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('aria-label', 'New Racer ID name');
    const status = el('div', 'rid-rename-status', form);
    status.setAttribute('aria-live', 'polite');
    const hint = `${USERNAME_MIN}-${USERNAME_MAX} LETTERS, NUMBERS, _ OR -`;
    status.textContent = hint;
    const row = el('div', 'rid-rename-btns', form);
    const save = el('button', 'btn btn-primary rid-rename-save', row);
    save.type = 'submit';
    save.textContent = 'SAVE';
    const cancel = el('button', 'btn rid-rename-cancel', row);
    cancel.type = 'button';
    cancel.textContent = 'CANCEL';

    const setStatus = (text: string, tone: '' | 'ok' | 'bad'): void => {
      status.textContent = text;
      status.className = `rid-rename-status${tone ? ` rid-rename-${tone}` : ''}`;
    };
    const close = (): void => {
      this.editingName = false;
      this.render(); // picks up any sync that landed while editing
    };

    // Live availability, debounced (the title editor's 450 ms beat).
    let seq = 0;
    input.addEventListener('input', () => {
      const v = input.value.trim();
      const mine = ++seq;
      if (v === '' || v.toLowerCase() === current.toLowerCase()) {
        setStatus(hint, '');
        return;
      }
      if (!isValidUsername(v)) {
        setStatus(hint, 'bad');
        return;
      }
      setStatus('CHECKING…', '');
      window.setTimeout(() => {
        if (mine !== seq || !this.cb.onCheckName) return;
        void this.cb.onCheckName(v).then((free) => {
          if (mine !== seq) return;
          if (free === true) setStatus('✓ AVAILABLE', 'ok');
          else if (free === false) setStatus('✗ NAME TAKEN', 'bad');
          else setStatus(hint, '');
        });
      }, 450);
    });
    // The menus listen for game keys — typing must never steer anything.
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Escape') close();
    });
    cancel.addEventListener('click', close);
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const name = input.value.trim();
      if (name === '' || name === current) {
        close();
        return;
      }
      if (!isValidUsername(name)) {
        setStatus(hint, 'bad');
        return;
      }
      seq++; // a pending availability answer must not overwrite the verdict
      save.disabled = true;
      cancel.disabled = true;
      input.disabled = true;
      setStatus('SAVING…', '');
      void claim(name).then((res) => {
        if (res.ok) {
          close();
          return;
        }
        save.disabled = false;
        cancel.disabled = false;
        input.disabled = false;
        input.focus();
        setStatus(
          res.reason === 'taken'
            ? '✗ NAME TAKEN — TRY ANOTHER'
            : res.reason === 'invalid'
              ? "✗ THAT NAME ISN'T ALLOWED"
              : res.reason === 'suspended' // M156: a moderator suspended this Racer ID
                ? '✗ THIS RACER ID IS SUSPENDED'
                : 'COULD NOT SAVE — CHECK YOUR CONNECTION',
          'bad',
        );
      });
    });
    input.focus();
    input.select();
  }

  /** M140: pending club/friend activity on the RACER CLUB button. */
  setCrewBadge(count: number): void {
    this.crewBadgeCount = count;
    const b = this.panel.querySelector<HTMLElement>('.rid-crew-badge');
    if (!b) return;
    b.textContent = count > 9 ? '9+' : String(count);
    b.style.display = count > 0 ? '' : 'none';
  }

  private setBusy(scope: HTMLElement, busy: boolean): void {
    this.busy = busy;
    for (const b of scope.querySelectorAll('button')) (b as HTMLButtonElement).disabled = busy;
  }

  hide(): void {
    this.editingName = false;
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}
